/**
 * Public API client.
 *
 * Used from route loaders, which run on the server for the first paint and in
 * the browser on client-side navigation. Nothing here carries credentials —
 * the public API is unauthenticated by design.
 */

const API_URL =
  typeof process !== "undefined" && process.env.API_URL
    ? `${process.env.API_URL}/api/v1`
    : (import.meta.env.VITE_API_URL ?? "http://localhost:4000/api/v1");

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function fetchApi<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { Accept: "application/json", ...init?.headers },
  });

  if (!response.ok) {
    let message = response.statusText;
    try {
      const problem = (await response.json()) as { detail?: string; title?: string };
      message = problem.detail ?? problem.title ?? message;
    } catch {
      /* keep the status text */
    }
    throw new ApiError(response.status, message);
  }

  return (await response.json()) as T;
}

/**
 * Fetch content, converting a 404 into a thrown Response.
 *
 * React Router turns a thrown Response into the route's error boundary with the
 * correct status, so a missing service returns a genuine 404 to crawlers rather
 * than a 200 page that merely says "not found".
 */
export async function fetchContent<T>(path: string): Promise<T> {
  try {
    return await fetchApi<T>(path);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      throw new Response("Not Found", { status: 404 });
    }
    throw error;
  }
}
